import { keepPreviousData, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PageMeta from '../../components/common/PageMeta';
import { useScopedQueryKey } from '../../hooks/useScopedQueryKey';
import { useSlashSearchShortcut } from '../../hooks/useSlashSearchShortcut';
import { formatPhone } from '../../shared/phone';
import { describeWhen } from '../../shared/time';
import {
  Avatar,
  Button,
  Callout,
  EmptyState,
  IconArrowRight,
  IconReceipt,
  IconSearch,
  Input,
  Kbd,
  ListPage,
  PageHeaderBand,
  Pagination,
  Segmented,
  Skeleton,
  Spinner,
  SummaryFact,
  SummaryFacts,
  Table,
  TableSkeletonRows,
  Tag,
  TBody,
  TD,
  TH,
  THead,
  TR,
  When,
} from '../../ui/v2';
import { phoneGlyph } from '../SmsInbox/smsModel';
import OrderDetailDialog from './OrderDetailDialog';
import {
  DEFAULT_ORDERS_ORDERING,
  fetchOrderStats,
  fetchOrdersPage,
  formatCurrencyFromCents,
  formatDollars,
  FULFILLMENT_LABEL,
  getCurrencyCode,
  itemCountLabel,
  listFulfillmentType,
  listStatusLabel,
  type Order,
  ORDER_SORT_FIELD_TO_UI,
  ORDER_SORT_UI_TO_FIELD,
  ORDER_STATUS_VIEWS,
  orderStatusTone,
  type OrderStatusView,
  ORDERS_PAGE_SIZE,
} from './orderModel';

/**
 * Reading order, as on Reservations: who ordered, how to reach them, when,
 * what and how much, how it's coming, where it stands. The order number
 * comes last in quieter type — it's a reference, not the story.
 */
const COLUMNS: {
  key: string;
  label: string;
  width?: number;
  align?: 'left' | 'right';
  sortable?: boolean;
}[] = [
  { key: 'customer_name', label: 'Customer', sortable: true },
  { key: 'phone', label: 'Phone', width: 140 },
  { key: 'created_at', label: 'Ordered', width: 196, sortable: true },
  { key: 'items', label: 'Items', width: 104 },
  {
    key: 'total_price',
    label: 'Total',
    width: 112,
    align: 'right',
    sortable: true,
  },
  { key: 'currency', label: 'Currency', width: 96, sortable: true },
  { key: 'type', label: 'Type', width: 96 },
  { key: 'status', label: 'Status', width: 128 },
  { key: 'id', label: 'Order', width: 96, sortable: true },
];

const STATUS_ITEMS = ORDER_STATUS_VIEWS.map((key) => ({ key, label: key }));

const MissingValue: React.FC = () => <span className="text-v2-fg-4">—</span>;

export default function OrderTable() {
  const [searchParams, setSearchParams] = useSearchParams();
  const orderingParam = searchParams.get('ordering') ?? DEFAULT_ORDERS_ORDERING;

  const { sortKey, sortOrder } = useMemo(() => {
    const desc = orderingParam.startsWith('-');
    const field = desc ? orderingParam.slice(1) : orderingParam;
    return {
      sortKey: ORDER_SORT_FIELD_TO_UI[field] ?? 'created_at',
      sortOrder: (desc ? 'desc' : 'asc') as 'asc' | 'desc',
    };
  }, [orderingParam]);

  const [status, setStatus] = useState<OrderStatusView>('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  useSlashSearchShortcut(searchRef);

  const { data: stats, isPending: statsLoading } = useQuery({
    queryKey: useScopedQueryKey('order-stats'),
    queryFn: fetchOrderStats,
  });
  const orderCount = stats?.orderCount ?? 0;
  const orderValueTotal = stats?.orderValueTotal ?? 0;
  const showTotalValue = statsLoading || orderValueTotal > 0;

  const queryKey = useScopedQueryKey(
    'orders',
    status,
    currentPage,
    ORDERS_PAGE_SIZE,
    orderingParam,
    debouncedSearch,
  );
  const { data, isPending, isError, error, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      fetchOrdersPage(
        currentPage,
        ORDERS_PAGE_SIZE,
        status,
        orderingParam,
        debouncedSearch,
      ),
    placeholderData: keepPreviousData,
  });

  const orders = useMemo(
    () => (data?.orders ?? []).filter((order) => order != null),
    [data],
  );
  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / ORDERS_PAGE_SIZE));
  const showInitialLoading = isPending && orders.length === 0;
  const fetchError =
    isError && error instanceof Error
      ? error.message
      : isError
        ? 'An unexpected error occurred. Please try again.'
        : null;
  const isRefreshing =
    isFetching && !showInitialLoading && !fetchError && orders.length > 0;
  const showPagination =
    !showInitialLoading && !fetchError && orders.length > 0;

  useEffect(() => {
    setCurrentPage(1);
  }, [status]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
    setCurrentPage(1);
  };

  const toggleSort = useCallback(
    (key: string) => {
      const targetField = ORDER_SORT_UI_TO_FIELD[key];
      if (!targetField) return;
      setCurrentPage(1);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        const prevRaw = prev.get('ordering') ?? DEFAULT_ORDERS_ORDERING;
        const prevDesc = prevRaw.startsWith('-');
        const prevField = prevDesc ? prevRaw.slice(1) : prevRaw;
        const newDesc = prevField !== targetField ? false : !prevDesc;
        next.set('ordering', newDesc ? `-${targetField}` : targetField);
        return next;
      });
    },
    [setSearchParams],
  );

  const emptyNoun = debouncedSearch ? 'matching orders' : 'orders';

  return (
    <>
      <PageMeta title="AskArthur | Order" description="AskArthur Order Page" />

      <ListPage
        title="Orders"
        actions={
          <>
            {isRefreshing && (
              <span
                role="status"
                className="inline-flex items-center gap-1.5 text-v2-12 text-v2-fg-3"
              >
                <Spinner />
                Updating orders...
              </span>
            )}
            <Link
              to="/refunds"
              className="inline-flex h-8 items-center gap-1 rounded-v2 px-2 text-v2-13 font-medium text-v2-accent hover:underline"
            >
              Refunds
              <IconArrowRight size={14} />
            </Link>
          </>
        }
        summary={
          <SummaryFacts>
            <SummaryFact
              label="Orders"
              value={
                statsLoading ? (
                  <Skeleton width={48} className="h-7" />
                ) : (
                  orderCount.toLocaleString()
                )
              }
            />
            {showTotalValue && (
              <SummaryFact
                label="Total order value"
                value={
                  statsLoading ? (
                    <Skeleton width={56} className="h-7" />
                  ) : (
                    formatDollars(orderValueTotal)
                  )
                }
              />
            )}
          </SummaryFacts>
        }
        toolbar={
          <PageHeaderBand>
            <div className="w-full min-w-0 sm:w-[320px] sm:shrink">
              <Input
                ref={searchRef}
                id="orders-search"
                type="search"
                aria-label="Search by name or phone"
                placeholder="Find an order by name or phone…"
                value={searchInput}
                onChange={handleSearchChange}
                leading={<IconSearch size={14} />}
                trailing={
                  <span className="hidden sm:inline-flex">
                    <Kbd>/</Kbd>
                  </span>
                }
              />
            </div>
            <Segmented
              aria-label="Order status"
              items={STATUS_ITEMS}
              value={status}
              onChange={setStatus}
              className="shrink-0"
            />
          </PageHeaderBand>
        }
      >
        {fetchError ? (
          <div className="p-4 lg:p-6">
            <Callout tone="danger" title="Couldn't load orders">
              {fetchError}
            </Callout>
          </div>
        ) : !showInitialLoading && orders.length === 0 ? (
          <EmptyState
            icon={<IconReceipt />}
            title={`You don't have any ${emptyNoun} at the moment`}
            description={`When ${emptyNoun} appear, they'll show up here.`}
          />
        ) : (
          <>
            {showInitialLoading && (
              <p role="status" className="sr-only">
                Loading orders…
              </p>
            )}
            <div
              className={clsx(
                'v2-scroll overflow-x-auto transition-opacity',
                isRefreshing && 'opacity-70',
              )}
            >
              <Table fixed className="min-w-[1120px]">
                <THead>
                  <tr>
                    {COLUMNS.map((column) => (
                      <TH
                        key={column.key}
                        width={column.width}
                        align={column.align}
                        sortable={column.sortable && !showInitialLoading}
                        active={column.sortable && sortKey === column.key}
                        dir={sortOrder}
                        onSort={() => toggleSort(column.key)}
                      >
                        {column.label}
                      </TH>
                    ))}
                  </tr>
                </THead>
                <TBody>
                  {showInitialLoading ? (
                    <TableSkeletonRows cols={COLUMNS.length} size="lg" />
                  ) : (
                    orders.map((order, index) => {
                      const fulfillment = listFulfillmentType(order);
                      const tone = orderStatusTone(order?.status);
                      // Cancelled and rejected orders stay listed but step back.
                      const muted = tone === 'danger';
                      // Today's live orders are what the floor acts on now.
                      const isToday =
                        !muted &&
                        describeWhen(order?.created_at)?.day === 'today';
                      return (
                        <TR
                          size="lg"
                          key={order?.id || `order-${index}`}
                          className={clsx(isToday && 'bg-v2-warning/[0.05]')}
                          data-today={isToday || undefined}
                        >
                          <TD size="lg" className="relative overflow-hidden">
                            {isToday && (
                              <span
                                aria-hidden
                                className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-sm bg-v2-warning"
                              />
                            )}
                            <div className="flex min-w-0 items-center gap-2.5">
                              <Avatar
                                name={order?.customer_name}
                                identity={order?.customer_phone}
                                fallback={phoneGlyph(order?.customer_phone)}
                                hue="auto"
                                className={clsx(muted && 'opacity-50')}
                              />
                              {order?.customer_name ? (
                                <span
                                  className={clsx(
                                    'truncate',
                                    muted
                                      ? 'font-medium text-v2-fg-3'
                                      : 'font-semibold text-v2-fg',
                                  )}
                                >
                                  {order.customer_name}
                                </span>
                              ) : (
                                <span className="truncate text-v2-fg-3">
                                  Unknown guest
                                </span>
                              )}
                            </div>
                          </TD>
                          <TD
                            size="lg"
                            num
                            className={clsx(
                              'whitespace-nowrap',
                              muted ? 'text-v2-fg-4' : 'text-v2-fg-2',
                            )}
                          >
                            {order?.customer_phone ? (
                              formatPhone(order.customer_phone)
                            ) : (
                              <MissingValue />
                            )}
                          </TD>
                          <TD size="lg" num className="whitespace-nowrap">
                            <When value={order?.created_at} muted={muted} />
                          </TD>
                          <TD size="lg">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setSelectedOrder(order)}
                              className="-ml-2.5"
                            >
                              {itemCountLabel(order)}
                            </Button>
                          </TD>
                          <TD
                            size="lg"
                            num
                            align="right"
                            className={clsx(
                              'font-medium',
                              muted && 'text-v2-fg-3',
                            )}
                          >
                            {formatCurrencyFromCents(
                              order?.total_price,
                              order?.currency,
                            )}
                          </TD>
                          <TD size="lg" className="text-v2-12 text-v2-fg-3">
                            {getCurrencyCode(order?.currency)}
                          </TD>
                          <TD size="lg">
                            {fulfillment === 'unknown' ? (
                              <span className="text-v2-fg-3">
                                {FULFILLMENT_LABEL.unknown}
                              </span>
                            ) : (
                              <Tag tone="neutral" caps>
                                {FULFILLMENT_LABEL[fulfillment]}
                              </Tag>
                            )}
                          </TD>
                          <TD size="lg">
                            <Tag tone={tone} caps>
                              {listStatusLabel(order)}
                            </Tag>
                          </TD>
                          <TD size="lg" num className="text-v2-12 text-v2-fg-3">
                            #{order?.id || 'N/A'}
                          </TD>
                        </TR>
                      );
                    })
                  )}
                </TBody>
              </Table>
            </div>
            {showPagination && (
              <Pagination
                page={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                pageSize={ORDERS_PAGE_SIZE}
                onPage={setCurrentPage}
                noun="orders"
              />
            )}
          </>
        )}
      </ListPage>

      <OrderDetailDialog
        order={selectedOrder}
        open={selectedOrder !== null}
        onClose={() => setSelectedOrder(null)}
      />
    </>
  );
}
